import { Document, pdf } from '@react-pdf/renderer';
import { fetchAllSKU } from 'api/sku';
import { washHub } from 'store';
import { Pickup } from 'types/pickup';
import { SKU } from 'types/sku';
import { errorToast } from 'utils/errorToast';
import { PickupDetailsPage } from './PickupDetailsPDFTemplate';

const MultiplePickupDetailsPdfTemplate = ({
  pickups,
  skuData,
  isRacked = false,
}: {
  pickups: Pickup[];
  skuData: Record<string, SKU[]>;
  isRacked?: boolean;
}) => {
  return (
    <Document>
      {pickups.map((pickup, index) => {
        const site = pickup.site?._id ?? pickup.collectionPoint?.site ?? '';
        const skuDataForSite = skuData[site];
        return (
          <PickupDetailsPage
            isRacked={isRacked}
            key={index}
            data={pickup}
            skuData={skuDataForSite}
          />
        );
      })}
    </Document>
  );
};

export const downloadPickups = async (
  pickups: Pickup[],
  options?: {
    fileName?: string;
    isRacked?: boolean;
  },
) => {
  const isRacked = options?.isRacked ?? false;
  if (!pickups?.length) {
    errorToast('No pickups found');
    return;
  }

  try {
    // Fetch SKU data for all sites in parallel
    const skuDataPromises = pickups.map(async (pickup) => {
      const site = pickup.site?._id ?? pickup.collectionPoint?.site ?? '';

      const response = await fetchAllSKU('item', undefined, site, true, true);
      return { site, data: response };
    });

    const skuDataResults = await Promise.all(skuDataPromises);
    const skuData = skuDataResults.reduce((acc, { site, data }) => {
      acc[site] = data;
      return acc;
    }, {} as Record<string, SKU[]>);

    // Generate PDF
    const fileName = options?.fileName
      ? options.fileName.includes('.pdf')
        ? options.fileName
        : `${options.fileName}.pdf`
      : 'Pickups.pdf';

    const blob = await pdf(
      <MultiplePickupDetailsPdfTemplate
        pickups={pickups}
        skuData={skuData}
        isRacked={isRacked}
      />,
    ).toBlob();

    // Download file
    const link = document.createElement('a');
    const blobUrl = window.URL.createObjectURL(blob);

    try {
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
    } finally {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }
  } catch (error) {
    console.error('Failed to download pickups:', error);
    throw error;
  }
};

export default MultiplePickupDetailsPdfTemplate;
